import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getSupabaseClient } from './supabase';

// ── Types ──────────────────────────────────────────────────────
export interface EventOverdueRow {
  event_id: string;
  status: string | null;
  site_location_id: string | null;
  event_end_date: string | null;
  event_start_date: string | null;
  is_invoice_overdue: boolean;
  is_pick_overdue: boolean;
  is_ship_overdue: boolean;
  is_return_overdue: boolean;
  is_urgent: boolean;
  is_overdue: boolean;
}

export type OverdueMap = Map<string, EventOverdueRow>;

// ── Helpers ────────────────────────────────────────────────────

/** Build a human-readable tooltip for an overdue event. */
export function overdueTooltip(row: EventOverdueRow): string {
  const parts: string[] = [];
  if (row.is_invoice_overdue) parts.push('Not invoiced');
  if (row.is_pick_overdue) parts.push('Not picked');
  if (row.is_ship_overdue) parts.push('Not shipped');
  if (row.is_return_overdue) parts.push('Not returned');
  return parts.length ? `Overdue: ${parts.join(', ')}` : '';
}

/** Build a human-readable tooltip for an urgent event. */
export function urgentTooltip(row: EventOverdueRow): string {
  if (!row.is_urgent) return '';
  const start = row.event_start_date;
  if (start) {
    const days = Math.max(0, Math.round((new Date(start).getTime() - Date.now()) / 86_400_000));
    return `Urgent: Event starts in ${days} day${days !== 1 ? 's' : ''}, still in ${row.status ?? 'quote'} stage`;
  }
  return 'Urgent: Event approaching, not yet confirmed';
}

// ── Data fetching ──────────────────────────────────────────────

/** Fetch overdue/urgent status for a single event. */
export async function fetchEventOverdueStatus(eventId: string): Promise<EventOverdueRow | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('event_overdue_status')
    .select('*')
    .eq('event_id', eventId)
    .maybeSingle();
  if (error) {
    console.warn('[overdue] Failed to fetch overdue status', error);
    return null;
  }
  return data as EventOverdueRow | null;
}

/** Fetch overdue/urgent status for multiple events (bulk). */
export async function fetchBulkOverdueStatus(eventIds: string[]): Promise<OverdueMap> {
  const map: OverdueMap = new Map();
  if (!eventIds.length) return map;
  const supabase = getSupabaseClient();
  // Supabase .in() has a practical limit; batch if needed
  const batchSize = 200;
  for (let i = 0; i < eventIds.length; i += batchSize) {
    const batch = eventIds.slice(i, i + batchSize);
    const { data, error } = await supabase
      .from('event_overdue_status')
      .select('*')
      .in('event_id', batch);
    if (error) {
      console.warn('[overdue] Bulk fetch failed', error);
      continue;
    }
    if (data) {
      for (const row of data as EventOverdueRow[]) {
        if (row.is_overdue || row.is_urgent) {
          map.set(row.event_id, row);
        }
      }
    }
  }
  return map;
}

// ── Hooks ──────────────────────────────────────────────────────

/** Hook: overdue/urgent status for a single event. */
export function useEventOverdueStatus(
  eventId: string | null | undefined,
): UseQueryResult<EventOverdueRow | null> {
  return useQuery({
    queryKey: ['event-overdue-status', eventId ?? null],
    queryFn: () => fetchEventOverdueStatus(eventId!),
    enabled: Boolean(eventId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Hook: bulk overdue/urgent status for a list of events (e.g. events library). */
export function useEventsOverdueMap(
  eventIds: string[],
): UseQueryResult<OverdueMap> {
  // Use a stable key based on sorted ids
  const key = eventIds.length > 0 ? eventIds.slice().sort().join(',') : '';
  return useQuery({
    queryKey: ['events-overdue-map', key],
    queryFn: () => fetchBulkOverdueStatus(eventIds),
    enabled: eventIds.length > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
